import { ENVIRONMENTS, getEnvironment } from '@hotjar/common';
import type { AxiosRequestConfig, AxiosStatic } from 'axios';
import { getAccessTokenHeaders, checkAndReloadAccessToken, logoutAndRedirect } from '@hotjar/auth';
import {
  isDemoSiteURL,
  redirectToDemoLogin,
} from '@hotjar/webapp/domains/acquisition/demo/demo-site-redirections';

export function is401ErrorCausedByPinnedPageOnReviewEnv(url = ''): boolean {
  const env = getEnvironment('INSIGHTS', window.location.host);
  if (env === ENVIRONMENTS.REVIEW || env === ENVIRONMENTS.REVIEW_WEBAPP) {
    return Boolean(url.match('/saved_heatmaps'));
  }

  return false;
}

export const setAxiosInterceptors = (axiosInstance: AxiosStatic): void => {
  axiosInstance.interceptors.request.use(
    async (req: AxiosRequestConfig) => {
      const tokenResult = await checkAndReloadAccessToken();
      const accessTokenHeaders = getAccessTokenHeaders(tokenResult);

      if (accessTokenHeaders) {
        req.headers = { ...req.headers, ...accessTokenHeaders };
      }
      return req;
    },
    (e) => Promise.reject(e),
  );

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (error) => {
      if (error?.response?.status === 401) {
        // This redirection is required so the folks who visit the recordings
        // from demo account, get redirected to login so they can see it.
        if (isDemoSiteURL(window.location.pathname, window.location.search)) {
          redirectToDemoLogin();
          return Promise.reject(error);
        }

        // HM-2031: Bypass 401s for pinned pages on review & review_webapp environments
        // FIXME: HM-2032 [FE] Cleanup 401 bypass for pinned pages on review environments
        if (is401ErrorCausedByPinnedPageOnReviewEnv(error?.config?.url ?? '')) {
          return Promise.reject(error);
        }

        await logoutAndRedirect();
      }

      // If we have blocked the user, log them out of Hotjar, show them a notification, then send them to the page
      // explaining the restrictions in place.
      if (error?.response?.status === 451) {
        window.location.href = 'https://www.hotjar.com/451';

        return null;
      }

      if (error?.response?.status === 503) {
        if (error?.response?.data && !!error?.response?.data.maintenance) {
          const event = new CustomEvent('feature-maintenance-mode', {
            detail: {
              messageKey: 'common:maintenance-mode-toast',
            },
          });

          window.dispatchEvent(event);
        } else {
          window.dispatchEvent(new CustomEvent('feature-maintenance-mode'));
        }
      }

      return Promise.reject(error);
    },
  );
};
